import { useState, useEffect } from 'react';

interface Job {
  job_id: string;
  date: string;
  service: string;
  city: string;
  duration: number;
  subtotal: number;
  commission: number;
  provider_payout: number;
}

interface Props {
  timeframe: { from: string; to: string };
  onViewDetails: (jobId: string) => void;
}

export default function JobEarningsTable({ timeframe, onViewDetails }: Props) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchJobs();
  }, [timeframe, page]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const token = localStorage.getItem('supabase_token');

      const url = new URL(`${supabaseUrl}/functions/v1/get-provider-earnings-jobs`);
      url.searchParams.append('from', timeframe.from);
      url.searchParams.append('to', timeframe.to);
      url.searchParams.append('page', page.toString());

      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setJobs(result.jobs);
        setTotalPages(result.totalPages);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="h-6 bg-gray-200 rounded w-48 mb-6 animate-pulse"></div>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-gray-100 rounded animate-pulse"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Job Earnings</h3>

        {/* Desktop Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Date</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Service</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">City</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Duration</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Customer paid</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Platform fee (15%)</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">You earned</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Details</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={job.job_id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-4 text-sm text-gray-900">{formatDate(job.date)}</td>
                  <td className="py-4 px-4 text-sm text-gray-900 capitalize">{job.service}</td>
                  <td className="py-4 px-4 text-sm text-gray-900">{job.city}</td>
                  <td className="py-4 px-4 text-sm text-gray-900">{formatDuration(job.duration)}</td>
                  <td className="py-4 px-4 text-sm text-gray-900 text-right">£{job.subtotal.toFixed(2)}</td>
                  <td className="py-4 px-4 text-sm text-red-600 text-right">−£{job.commission.toFixed(2)}</td>
                  <td className="py-4 px-4 text-sm font-semibold text-teal-600 text-right">£{job.provider_payout.toFixed(2)}</td>
                  <td className="py-4 px-4 text-center">
                    <button
                      onClick={() => onViewDetails(job.job_id)}
                      className="text-teal-600 hover:text-teal-700 text-sm font-medium whitespace-nowrap"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden space-y-4">
          {jobs.map((job) => (
            <div key={job.job_id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-sm font-medium text-gray-900 capitalize">{job.service}</div>
                  <div className="text-xs text-gray-500 mt-1">{formatDate(job.date)} • {job.city}</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-teal-600">£{job.provider_payout.toFixed(2)}</div>
                  <div className="text-xs text-gray-500">You earned</div>
                </div>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <div className="text-xs text-gray-600">
                  Duration: {formatDuration(job.duration)}
                </div>
                <button
                  onClick={() => onViewDetails(job.job_id)}
                  className="text-teal-600 hover:text-teal-700 text-sm font-medium whitespace-nowrap"
                >
                  View details
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              Next
            </button>
          </div>
        )}

        {jobs.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-file-list-line text-4xl text-gray-300 mb-3"></i>
            <p className="text-gray-500">No completed jobs in this timeframe</p>
          </div>
        )}
      </div>
    </div>
  );
}
